import React from 'react';
import { Link } from 'react-router-dom';
import HomeNavbar from './HomeNavbar';

function About() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0e27] to-[#0f172a]">
      <HomeNavbar />
      <div className="pt-28 pb-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              About ConvrsAI by Market Wavegen
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Revenue Validation and Qualification Platform
            </p>
          </div>

          {/* Main Content */}
          <div className="space-y-12">
            {/* Introduction */}
            <section className="bg-[#1a1f3a]/80 border border-blue-500/30 rounded-xl p-8 sm:p-10 backdrop-blur-sm">
              <h2 className="text-2xl font-bold text-white mb-6">Our Mission</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                ConvrsAI by Market Wavegen ensures only real, verified buying intent reaches your sales team. Most demand systems detect interest. ConvrsAI by Market Wavegen proves buyer reality before sales ever engages.
              </p>
              <p className="text-gray-300 leading-relaxed">
                ConvrsAI by Market Wavegen sits on top of any lead source, campaign, or signal engine and acts as a revenue validation layer filtering, qualifying, and confirming demand so sales teams engage only with leads that are real, relevant, and ready.
              </p>
              <p className="text-blue-400 font-semibold text-lg mt-6">
                You don't pass activity to sales. You pass validated buying intent.
              </p>
            </section>

            {/* What We Do */}
            <section className="bg-[#1a1f3a]/80 border border-blue-500/30 rounded-xl p-8 sm:p-10 backdrop-blur-sm">
              <h2 className="text-2xl font-bold text-white mb-6">What We Do</h2>
              <p className="text-gray-300 leading-relaxed mb-6">
                ConvrsAI by Market Wavegen is a Revenue Validation Platform and Qualification Engine designed to ensure every lead entering your CRM represents a real buying opportunity. It evaluates each lead as a risk decision not just a record.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="text-2xl flex-shrink-0 mt-1">✓</div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold mb-2">Validates The Business</h3>
                    <p className="text-gray-400">Company legitimacy, website presence, industry classification, geographic relevance</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="text-2xl flex-shrink-0 mt-1">✓</div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold mb-2">Validates The Buyer</h3>
                    <p className="text-gray-400">Job title accuracy, role seniority, decision influence, department relevance</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="text-2xl flex-shrink-0 mt-1">✓</div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold mb-2">Validates The Intent</h3>
                    <p className="text-gray-400">AI-led email and voice qualification, conversation intelligence</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="text-2xl flex-shrink-0 mt-1">✓</div>
                  <div className="flex-1">
                    <h3 className="text-white font-semibold mb-2">Validates The Readiness</h3>
                    <p className="text-gray-400">Firmographic fit, timing indicators, confidence scoring</p>
                  </div>
                </div>
              </div>
              <p className="text-blue-400 font-semibold mt-6">
                Before sales touches the lead.
              </p>
            </section>

            {/* How It Works */}
            <section className="bg-[#1a1f3a]/80 border border-blue-500/30 rounded-xl p-8 sm:p-10 backdrop-blur-sm">
              <h2 className="text-2xl font-bold text-white mb-6">How It Works</h2>
              <p className="text-gray-300 leading-relaxed mb-6">
                ConvrsAI by Market Wavegen ingests leads from any source and applies multiple validation and qualification layers:
              </p>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start gap-3">
                  <span className="text-blue-400 font-bold">•</span>
                  <span>Deep web data validation</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-400 font-bold">•</span>
                  <span>Multi-API firmographic verification</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-400 font-bold">•</span>
                  <span>Role and title relevance checks</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-400 font-bold">•</span>
                  <span>AI-led email and voice qualification</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-400 font-bold">•</span>
                  <span>Conversation intelligence and confidence scoring</span>
                </li>
              </ul>
              <p className="text-gray-300 leading-relaxed mt-6">
                Every step removes uncertainty and prevents waste before it hits your sales motion.
              </p>
            </section>

            {/* Built For */}
            <section className="bg-gradient-to-r from-blue-600/20 to-blue-800/20 border border-blue-500/30 rounded-xl p-8 sm:p-10 backdrop-blur-sm">
              <h2 className="text-2xl font-bold text-white mb-6">Built for Modern B2B Revenue Teams</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                ConvrsAI by Market Wavegen works across:
              </p>
              <ul className="space-y-2 text-gray-300 mb-6">
                <li>• All B2B industries</li>
                <li>• Any lead source or campaign</li>
                <li>• Any CRM or sales workflow</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                If your business depends on pipeline quality, ConvrsAI by Market Wavegen becomes a critical control layer in your revenue system.
              </p>
            </section>

            {/* CTA */}
            <div className="text-center">
              <a
                href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ1LRerQzILalOQbfT9l5lWQ6abwrkk6n45IbqR_cPsf8tH09a8kVlButSqrKMTkMo1De-h8vUdB"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-lg transition-all shadow-lg shadow-blue-600/30 hover:shadow-blue-600/50"
              >
                Book a Demo
              </a>
            </div>
          </div>

          {/* Footer */}
          <footer className="mt-16 pt-8 border-t border-blue-500/20">
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Link to="/terms" className="text-blue-400 hover:text-blue-300 transition-colors">
                Terms and Conditions
              </Link>
              <span className="text-gray-600">•</span>
              <Link to="/privacy-policy" className="text-blue-400 hover:text-blue-300 transition-colors">
                Privacy Policy
              </Link>
            </div>
            <p className="text-center text-gray-500 text-xs mt-4">
              © 2026 ConvrsAI by Market Wavegen. All rights reserved.
            </p>
          </footer>
        </div>
      </div>
    </div>
  );
}

export default About;
